<script setup lang="ts">
import CSchoolDetails from '@/presentation/components/schools/CSchoolDetails.vue'
import CSchoolPeriods from '@/presentation/components/schools/CSchoolPeriods.vue'
import CSchoolPeriodPedagogicalTeam from '@/presentation/components/schools/CSchoolPeriodPedagogicalTeam.vue'
import CSchoolPeriodStudents from '@/presentation/components/schools/CSchoolPeriodStudents.vue'
import CSchoolClassrooms from '@/presentation/components/schools/CSchoolClassrooms.vue'
import CSchoolAssignments from '@/presentation/components/schools/CSchoolAssignments.vue'
import PTabMenu from 'primevue/tabmenu'
import PProgressSpinner from 'primevue/progressspinner'
import PMessage from 'primevue/message'
import PButton from 'primevue/button'
import { useSchoolStore } from '@/presentation/stores/school/school.ts'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, watch } from 'vue'
import { useSchoolPeriodStore } from '@/presentation/stores/school/schoolPeriod.ts'
import { useSchoolPeriodUserStore } from '@/presentation/stores/school/schoolPeriodUser.ts'
import { useSchoolClassroomStore } from '@/presentation/stores/school/schoolClassroom.ts'
import { useAssignmentStore } from '@/presentation/stores/school/assignment.ts'

// COMPOSABLES
const router = useRouter()
const route = useRoute()
const schoolStore = useSchoolStore()
const schoolPeriodStore = useSchoolPeriodStore()
const schoolPeriodUserStore = useSchoolPeriodUserStore()
const schoolClassroomStore = useSchoolClassroomStore()
const assignmentStore = useAssignmentStore()

// STORE REFS
const { selectedSchool, schoolStoreError } = storeToRefs(schoolStore)
const { selectedSchoolPeriod } = storeToRefs(schoolPeriodStore)

// LOCAL REFS
const activeTab = ref('Informations générales')
const localLoading = ref(false)
const localError = ref('')

// TAB CONFIGURATION
const tabs = {
  INFO: 'Informations générales',
  PERIODS: 'Périodes scolaires',
  ASSIGNMENTS: 'Gestion des parcours',
  TEAM: 'Équipe pédagogique',
  STUDENTS: 'Élèves',
  CLASSES: 'Gestion des classes et groupes'
}

// PROPS
const props = defineProps<{
  schoolSlug: string
  loading?: boolean
}>()

const items = ref([
  createTabItem(tabs.INFO),
  createTabItem(tabs.PERIODS),
  createTabItem(tabs.ASSIGNMENTS),
  createTabItem(tabs.TEAM),
  createTabItem(tabs.STUDENTS),
  createTabItem(tabs.CLASSES)
])

// METHODS
/**
 * Gère le changement d'onglet et met à jour l'URL
 */
const onTabChange = async (tabName: string) => {
  activeTab.value = tabName
  await router.push({ query: { ...route.query, tab: tabName } })

  // Charger les données du nouveau tab
  await loadActiveTabData()
}

/**
 * Crée un élément de menu pour les onglets
 */
function createTabItem(label: string) {
  return {
    label,
    command: () => onTabChange(label)
  }
}

/**
 * Initialise l'onglet à partir des paramètres de l'URL
 */
function initializeTab() {
  // Mettre à jour l'onglet sélectionné en fonction de l'URL
  if (route.query.tab && typeof route.query.tab === 'string') {
    activeTab.value = route.query.tab
  } else {
    router.replace({ query: { ...route.query, tab: activeTab.value } })
  }
}

/**
 * Charge les données du tab actif
 */
async function loadActiveTabData() {
  if (!school.value || !selectedSchoolPeriod.value) return

  try {
    localLoading.value = true
    localError.value = ''

    const schoolId = school.value.id
    const periodId = selectedSchoolPeriod.value.schoolPeriodId

    // Charger les données en fonction du tab actif
    switch (activeTab.value) {
      case tabs.INFO:
        // Pas de données à charger pour les informations générales
        break

      case tabs.PERIODS:
        // Les périodes sont déjà chargées par le router guard
        break

      case tabs.ASSIGNMENTS:
        // Charger les assignements et modules valides
        await assignmentStore.loadInitialData(schoolId, periodId, true)
        break

      case tabs.TEAM:
        // Charger l'équipe pédagogique
        await schoolPeriodUserStore.fetchAllSchoolPeriodUsersForSchool(schoolId, periodId, true)
        break

      case tabs.STUDENTS:
        // Charger les étudiants
        await schoolPeriodUserStore.fetchAllSchoolPeriodUsersForSchool(schoolId, periodId, true)
        break

      case tabs.CLASSES:
        // Charger les classes
        await schoolClassroomStore.fetchAllSchoolClassroomsForSchoolPeriod(schoolId, periodId, true)
        break
    }
  } catch (err) {
    localError.value = err instanceof Error ? err.message : 'Erreur lors du chargement des données'
    console.error('Erreur lors du chargement des données du tab:', err)
  } finally {
    localLoading.value = false
  }
}

// COMPUTED
/**
 * Récupère l'école active depuis le store
 */
const school = computed(() => schoolStore.getSchoolBySlug(props.schoolSlug))

/**
 * Composant à afficher en fonction de l'onglet actif
 */
const tabDisplay = computed(() => {
  switch (activeTab.value) {
    case tabs.INFO:
      return CSchoolDetails
    case tabs.PERIODS:
      return CSchoolPeriods
    case tabs.ASSIGNMENTS:
      return CSchoolAssignments
    case tabs.TEAM:
      return CSchoolPeriodPedagogicalTeam
    case tabs.STUDENTS:
      return CSchoolPeriodStudents
    case tabs.CLASSES:
      return CSchoolClassrooms
    default:
      return 'div'
  }
})

/**
 * Index de l'onglet actif pour le menu
 */
const activeTabIndex = computed(() => {
  return items.value.findIndex((item) => item.label === activeTab.value)
})

/**
 * État de chargement global, utilise le prop loading du parent
 */
const isLoading = computed(() => localLoading.value || props.loading)

/**
 * Message d'erreur global (combine les erreurs locales et du store)
 */
const errorMessage = computed(() => {
  if (localError.value) return localError.value
  if (schoolStoreError.value.hasError) return schoolStoreError.value.message
  return ''
})

/**
 * Vérifie si une école est sélectionnée et si elle correspond au slug
 */
const isCorrectSchoolSelected = computed(() => {
  return selectedSchool.value?.slug === props.schoolSlug
})

// LIFECYCLE
onMounted(async () => {
  // Initialiser l'onglet et vérifier que tout est bien configuré
  initializeTab()

  if (!selectedSchool.value) { schoolStore.selectSchool(school.value ?? null) }

  // Note: Les périodes scolaires sont déjà chargées par le router guard
  // Charger les données du tab actif
  await loadActiveTabData()
})

// WATCHERS
// Observer les changements de slug d'école pour mettre à jour l'école sélectionnée
watch(
  () => props.schoolSlug,
  (newSlug) => {
    // Mettre à jour l'école sélectionnée si nécessaire
    const newSchool = schoolStore.getSchoolBySlug(newSlug)
    if (newSchool && !isCorrectSchoolSelected.value) {
      schoolStore.selectSchool(newSchool)
    }

    // Réinitialiser l'onglet
    initializeTab()
  }
)
</script>

<template>
  <div class="flex flex-col gap-4">
    <div>
      <p-tab-menu :model="items" :activeIndex="activeTabIndex" />
    </div>

    <!-- Message d'erreur local -->
    <p-message v-if="errorMessage" severity="error" class="w-full">
      {{ errorMessage }}
    </p-message>

    <!-- Affichage pendant le chargement -->
    <div v-if="isLoading" class="flex justify-center p-6">
      <p-progress-spinner strokeWidth="4" />
    </div>

    <!-- Affichage de l'école -->
    <template v-else-if="school">
      <component :is="tabDisplay" :school="selectedSchool" class="mt-4" />
    </template>

    <!-- Message si l'école n'est pas trouvée -->
    <div v-else class="bg-surface-200 dark:bg-surface-600 rounded-border p-6">
      <p>Établissement non trouvé</p>
      <p-button
        label="Retour à la liste"
        icon="pi pi-arrow-left"
        class="mt-4"
        @click="router.push({ name: 'schools-list' })"
      />
    </div>
  </div>
</template>
